import type { Money } from "@commercekit/shared";

export enum CountryCode {
  NL = "Nederland",
  BE = "België",
  FR = "France",
  DE = "Germany",
  PL = "Poland",
  PT = "Portugal",
  DK = "Denmark",
  ES = "Spain",
  GB = "United Kingdom",
  IE = "Ireland",
  IT = "Italy",
  SE = "Sweden",
  AT = "Austria",
}

export enum CustomerType {
  B2C = "B2C",
  B2B = "B2B",
  CONTRACT = "CONTRACT",
}

type Boolean = boolean;
type Float = number;
type Int = number;
type String = string;

export interface Image {
  url: string;
  altText: string;
}

export interface ResponsiveImageAlternative {
  type: string;
  url: string;
}

export interface ResponsiveImage {
  urlThumbnail: string;
  urlOriginal: string;
  altText: string;
  alternativeSizes: ResponsiveImageAlternative[];
}

export interface SeoDetails {
  metaDescription: string;
  metaKeywords: string;
  pageTitle: string;
}

export interface WovarSeoDetails {
  headingTag: string;
  pageTitle: string;
  metaDescription: string;
  metaKeywords: string;
}

export interface Inventory {
  isInStock: Boolean;
  availableToSell?: Int;
  warningLevel?: Int;
}

export interface StrapiPagination {
  page: Int;
  totalPages: Int;
  total: Int;
}

export interface Pagination {
  endCursor: String;
  startCursor: String;
  hasNextPage: Boolean;
  total?: Int;
}

export interface Measure {
  value: Float;
  unit: String;
}

export interface CustomerFieldValue {
  stringValue: String;
  stringValues: String[];
}

export interface CustomerFields {
  displayName: string;
  name: String;
  value: CustomerFieldValue;
}

interface ProductBase {
  sku: String;
  name: String;
  description: String;
  priceInclTax: Money;
  inventory: Inventory;
  categories: Category[];
}
export interface ProductOption {
  id: Int;
  name: String;
  values: { id: Int; label: String; isDefault: Boolean }[];
}
export interface Product extends ProductBase {
  assets: {
    url: String;
    name: String;
  }[];
  id: String;
  path: String;
  // width?: Measure;
  // height?: Measure;
  // depth?: Measure;
  weight?: Measure;
  priceExclTax: Money;
  priceInclTax: Money;
  salePriceExclTax?: Money;
  salePriceInclTax?: Money;
  seo: SeoDetails;
  defaultImage: ResponsiveImage;
  images: ResponsiveImage[];
  // type: String;
  // gtin: String;
  customFields: CustomerFields[];
  plainTextDescription: String;
  relatedProducts: Product[];
  notes: String;
  variants: {
    id: String;
    sku: String;
    productOptions: ProductOption[];
    product: Pick<Product, "path" | "categories">;
  }[];
  withContract: Boolean;
  slices: (FullWidthImageSlice | ColumnsTextCtaSlice)[];
}

export interface Breadcrumb {
  id: String;
  name: String;
  path: String;
  fullPath?: String;
}

export interface Category {
  id: String;
  name: String;
  path: String;
  fullPath: String;
  // description: String;
  seo: SeoDetails;
  breadcrumbs: Breadcrumb[];
  children: Category[];
}

export interface WovarCategory {
  id: String;
  name: String;
  path: String;
  fullPath: String;
  description: String;
  slug: String;
  shortDescription: String;
  longDescription: String;
  images: ResponsiveImage[];
  seo: WovarSeoDetails;
  breadcrumbs: Breadcrumb[];
  children: WovarCategory[];
  localizedSlugs: LocalizedSlug[];
}

export interface ProductAttributeSearchFilter {
  name: String;
  options: ProductAttributeSearchFilterOption[];
}

export interface ProductAttributeSearchFilterOption {
  name: String;
  key: String;
  value: String;
  count: Number;
}

export interface CheckoutLineItem {
  image: Image;
  id: String;
  productId: String;
  sku: String;
  name: String;
  slug: String;
  quantity: Int;
  uomProperty: String;
  priceInclTax?: Money;
  totalInclTax?: Money;
  priceExclTax?: Money;
  totalExclTax?: Money;
  originalPrice?: Money;
  originalTotal: Money;
  price: Money;
  total: Money;
  product: WovarProduct;
  valid: Boolean;
}

export interface WovarCheckoutLineItem {
  priceExclTax: Money;
  totalExclTax: Money;
  deliveryEstimateMessage: ProductDeliveryEstimateEnum;
  uomProperty: String;
  id: String;
  productId: String;
  variantId: String;
  sku: String;
  name: String;
  slug: String;
  quantity: Int;
  price: Money;
  total: Money;
  originalTotal: Money;
  originalPrice: Money;
  priceInclTax: Money;
  totalInclTax: Money;
  image: Image;
  productDiscountTotal: Money;
  saleTotal: Money;
  subTotalInclTax: Money;
  isSample: Boolean;
  path: String;
  product: WovarProduct;
  valid: Boolean;
}
export interface WovarCustomLineItem {
  price: Money;
  totalExclTax: Money;
  id: string;
  sku: string;
  name: string;
  description: string;
  extraInfo: string;
}

export interface CheckoutCustomLineItem {
  id: String;
  extendedPrice: Money;
  sku: String;
  name: String;
  quantity: Int;
  price: Money;
}

export interface Cart {
  id: String;
  lineItems: CheckoutLineItem[];
  taxTotal: Money;
  subtotalExclTax: Money;
  subtotalInclTax: Money;
}

export enum AddressType {
  SHIPPING = "SHIPPING",
  BILLING = "BILLING",
  HEADQUARTERS = "HEADQUARTERS",
}

export enum PickupOrDeliveryEnum {
  DELIVERY = "DELIVERY",
  PICKUP = "PICKUP",
}

export enum Gender {
  MALE = "MALE",
  FEMALE = "FEMALE",
  GENDERLESS = "GENDERLESS",
}

// export enum CompanyAddressType {
//   SHIPPING = "SHIPPING",
//   BILLING = "BILLING",
//   HEADQUARTERS = "HEADQUARTERS",
// }

export enum SliceType {
  FULL_WIDTH_IMAGE = "slices.full-width-image",
  COLUMNS_TEXT_CTA = "slices.columns-text-cta",
}

export interface Address {
  firstName: String;
  lastName: String;
  company: String;
  email?: String;
  address1: String;
  address2: String;
  city: String;
  countryCode: keyof typeof CountryCode;
  postalCode: String;
  stateOrProvince: String;
  streetNumber?: String;
  purchaseReference?: String;
  additionalStreetInfo?: String;
  vatNumber?: String;
  kvkNumber?: String;
  id: String;
}

export interface CustomerAddress extends Address {
  phone: String;
  type: AddressType;
  isDefault: boolean;
}

export interface BillingAddress extends Address {
  phone: String;
  streetNumber?: String;
}

export interface Coupon_ {
  code: String;
  discount: Money;
}

export interface CheckoutSimple {
  subtotalInclTax: Money;
  subtotalExclTax: Money;
  lineItems: (Pick<
    CheckoutLineItem,
    "name" | "quantity" | "total" | "totalInclTax" | "totalExclTax" | "originalTotal" | "id" | "sku" | "uomProperty" | "slug" | "valid"
  > & {
    image: Pick<Image, "url" | "altText">;
  } & {
    product: Pick<WovarProduct, "slug">;
  })[];
}
export interface Checkout extends CheckoutSimple {
  id: String;
  lineItems: CheckoutLineItem[];
  taxTotal: Money;
  discountTotal: Money;
  shippingCostInclTax: Money;
  shippingCostExclTax: Money;
  handlingCostInclTax?: Money;
  handlingCostExclTax?: Money;
  totalInclTax: Money;
  totalExclTax: Money;
  billingAddress?: BillingAddress;
  shippingAddress?: Address;
  coupon?: Coupon_;
  coupons?: Coupon_[];
  comments: String;
  relatedProducts: Product[];
}

export interface WovarCheckout extends CheckoutSimple {
  id: String;
  lineItems: WovarCheckoutLineItem[];
  customLineItems: CheckoutCustomLineItem[];
  taxTotal?: Money;
  discountTotal?: Money;
  shippingCostInclTax: Money;
  shippingCostExclTax: Money;
  handlingCostInclTax?: Money;
  handlingCostExclTax?: Money;
  shippingOption?: ShippingOption;
  totalInclTax: Money;
  totalExclTax: Money;
  originalTotal?: Money;
  billingAddress?: BillingAddress;
  shippingAddress?: Address;
  pickupOrDelivery?: PickupOrDeliveryEnum;
  productDiscountTotal?: Money;
  coupon?: Coupon_;
  coupons?: Coupon_[];
  comments: String;
  customerCompanyName?: String;
  customerEmail?: String;
  customerFirstName?: String;
  customerId?: String;
  customerKvkNumber?: String;
  customerLastName?: String;
  customerPhone?: String;
  customerVatNumber?: String;
  relatedProducts: WovarProduct[];
  checkoutRules?: {
    allowOrder: boolean;
    smallOrderThreshold: number;
  };
}

export interface Coupon extends Checkout {}

export interface ToFreeShipping {
  remnantToFreeShipping: number;
  remnantToFreeShippingPercent: number;
  currencyCode?: string;
}

// export interface CompanyAddress {
//   id: String;
//   type: CompanyAddressType;
//   isDefault: Boolean;
//   firstName: String;
//   lastName: String;
//   company: String;
//   address1: String;
//   address2: String;
//   city: String;
//   countryCode: keyof typeof CountryCode;
//   postalCode: String;
//   stateOrProvince: String;
//   phone: String;
// }
export interface CompanyUser {
  id: String;
  firstName: String;
  lastName: String;
  email: String;
  phone: String;
  role: Role;
}
export interface Company {
  id: String;
  name: String;
  payByInvoice: Boolean;
  addresses: CustomerAddress[];
  defaultBillingAddress: CustomerAddress | null;
  defaultShippingAddress: CustomerAddress | null;
  defaultHeadquartersAddress: CustomerAddress | null;
}
type Role = "ADMIN" | "MANAGER" | "SALES_REPRESENTATIVE" | "COMPANY_ADMIN" | "COMPANY_BUYER";

export interface Customer {
  id: String;
  firstName: String;
  lastName: String;
  middleName: String;
  email: String;
  companyName: String;
  phone: String;
  birthDate: String;
  gender: Gender;
  addresses: CustomerAddress[];
  defaultBillingAddress: CustomerAddress;
  defaultShippingAddress: CustomerAddress;
  customerGroupId: String;
  type: CustomerType;
  vatNumber: String;
  role: Role | null;
  company: Company | null;
  kvkNumber: String;
}

export interface WovarCustomer {
  id: String;
  firstName: String;
  lastName: String;
  middleName: String;
  email: String;
  companyName: String;
  phone: String;
  birthDate: String;
  gender: Gender;
  addresses: CustomerAddress[];
  defaultBillingAddress: CustomerAddress;
  defaultShippingAddress: CustomerAddress;
  customerGroupId: String;
  type: CustomerType;
  vatNumber: String;
  newsLetter: Boolean;
  role: Role | null;
  company: Company | null;
  kvkNumber: String;
  payByInvoice: String;
}

export type CustomerSimple = Pick<Customer, "id" | "firstName" | "lastName" | "middleName" | "type" | "email">;
export interface PaymentMethods {
  id: String;
  name: String;
  code: String;
  image: String;
  provider: String;
  charge?: Float;
}

export interface ShippingOption {
  isDefault: Boolean;
  id: String;
  key: String;
  description: String;
  name: String;
  countryCode: String;
  imageUrl: String;
  cost: Money;
  toFreeShipping: Money;
  totalInclTax: Money;
  subTotalInclTax: Money;
  totalExclTax: Money;
  transitTime: String;
}

export interface OrderLineItem {
  id: String;
  productId: String;
  sku: String;
  name: String;
  quantity: Int;
  uomProperty: String;
  priceExclTax: Money;
  totalExclTax: Money;
  priceInclTax: Money;
  totalInclTax: Money;
  product?: Product;
}
export interface OrderBase {
  id: String;
  orderNumber: String;
  lineItems: OrderLineItem[];
  totalInclTax: Money;
  totalExclTax: Money;
}

export interface OrderSimple extends OrderBase {
  estimatedDeliveryDate: string;
  taxTotal: Money;
  shippingCostInclTax: Money;
  shippingCostExclTax: Money;
  subtotalExclTax: Money;
  discountTotal: Money;
  coupon: {
    discount: Money;
  };
}

export type OrderStatusStage =
  | "NOT_REGISTERED_IN_WAREHOUSE"
  | "SHIPPED_TO_CARRIER";

export type OrderInvoiceStatus = "OPEN" | "PARTIALLY_PAID" | "PAID_IN_FULL";

export interface OrderPaymentMethod {
  name?: String;
  code?: String;
}

// Deposits / cut-to-size fees / surcharges — separate from regular lineItems.
export interface OrderCustomLineItem {
  id: String;
  name?: String;
  quantity?: number;
  price?: Money;
  total?: Money;
}

export interface OrderCoupon {
  code?: String;
}

export interface OrderShippingOption {
  description?: String;
  transitTime?: String | number;
}

export interface OrderDetail extends OrderBase {
  shippingAddress: BillingAddress;
  billingAddress: BillingAddress;
  status: String;
  // WOV-2138 order-overview enrichment (flat on WovarGetOrderResponse).
  statusStage?: OrderStatusStage;
  paymentMethod?: OrderPaymentMethod;
  transporter?: String;
  trackAndTraceCodes?: String[];
  invoiceNumber?: String;
  invoiceStatus?: OrderInvoiceStatus;
  estimatedDeliveryDate?: String;
  paymentReference?: String;
  paymentIBAN?: String;
  paymentBankName?: String;
  shippingOption?: OrderShippingOption;
  coupon?: OrderCoupon;
  customLineItems?: OrderCustomLineItem[];
  subtotalInclTax: Money;
  subtotalExclTax: Money;
  taxTotal: Money;
  discountTotal: Money;
  shippingCostInclTax: Money;
  handlingCostInclTax?: Money;
  orderDate: String;
  totalExclTax: Money;
}

export interface SliceImage {
  url: string;
  alternativeText: string;
  width: number;
}
export interface FullWidthImageSlice {
  type: SliceType.FULL_WIDTH_IMAGE;
  data: {
    backgroundColor: string;
    responsive: {
      viewport: "LARGE" | "MEDIUM" | "SMALL";
      image: SliceImage;
    }[];
  };
}

export interface ColumnsTextCtaSlice {
  type: SliceType.COLUMNS_TEXT_CTA;
  data: {
    columns: {
      content: string;
      image: SliceImage;
      link: string;
    }[];
  };
}

export interface CursorPagination {
  hasNextPage: Boolean;
  startCursor: String;
  endCursor: String;
  total: Int;
}

export interface LocalizedSlug {
  locale: String;
  url: String;
}

export interface BlogLocalizedSlug {
  lang: string;
  uid: string;
}
export interface SitemapSlug {
  slug?: string;
  imageUrl?: string;
  localizedSlugs: LocalizedSlug[];
}

export interface FreeShippingInfo {
  isEligible: boolean;
  threshold?: Money;
  isFreeShipping: boolean;
}

export interface WovarProduct {
  marketingProperties: [CustomField];
  advantages: [CustomField];
  disadvantages: [CustomField];
  id: String;
  name: String;
  path: String;
  fullPath: String;
  type: String;
  sku: String;
  slug: String;
  description: String;
  notes: String;
  weight: Measure;
  width: Measure;
  depth: Measure;
  height: Measure;
  priceExclTax: Money;
  discountedPriceExclTax: Money;
  salePriceExclTax: Money;
  priceInclTax: Money;
  discountedPriceInclTax: Money;
  salePriceInclTax: Money;
  recommendedPriceInclTax: Money;
  recommendedPriceExclTax: Money;
  shippingPriceInclTax?: Money;
  shippingPriceExclTax?: Money;
  freeShippingInfo?: FreeShippingInfo;
  seo: SeoDetails;
  defaultImage: ResponsiveImage;
  images: [ResponsiveImage];
  inventory: Inventory;
  gtin: String;
  categories: [WovarCategory];
  customFields: [CustomField];
  bulkPricing: [BulkPricingTier];
  rank: String;
  unitsOfMeasure: [UnitOfMeasure];
  localizedSlugs: [LocalizedSlug];
  variants: [ExtendedProductVariant];
  productOptions: [ProductOption];
  customizations: [ProductCustomization];
  slices: (FullWidthImageSlice | ColumnsTextCtaSlice)[];
  relatedProducts: [WovarProduct];
  productAssociations: [ProductAssociation];
  withContract: Boolean;
  plainTextDescription: string;
  assets: {
    url: String;
    name: String;
  }[];
  productUnit: string;
  stockLevel: Int;
  availableForPurchase: Boolean;
  videos: {
    name: String;
    url: String;
  }[];
  // plainTextDescription(
  //   characterLimit: Int = 200
  //   ellipsis: String = "..."
  // ): String
}

export interface CustomField {
  name: String;
  displayName: String;
  value: CustomFieldValue;
}

export interface CustomFieldValue {
  key: String;
  stringValue: String;
  stringValues: [String];
}

export interface CustomCheckField {
  name: String;
  displayName: String;
  value: CustomCheckFieldValue;
}

export interface CustomCheckFieldValue {
  key: String;
  stringValue: String;
  stringValues: CustomCheckBoxValueField[];
}

export interface CustomCheckBoxValueField {
  label: String;
  isChecked: Boolean;
}

export interface BulkPricingTier {
  minimumQuantity: Int;
  maximumQuantity: Int;
  fixedPriceDiscount: Money;
  percentageDiscount: Money;
  relativePriceDiscount: Money;
}

export interface ProductVariant {
  id: String;
  sku: String;
  defaultImage: Image;
  priceInclTax: Money;
  priceExclTax: Money;
  salePriceInclTax: Money;
  salePriceExclTax: Money;
  stock: Int;
  customFields: [CustomField];
  productOptions: [ProductOption];
}

export interface ExtendedProductVariant {
  name: String;
  slug: String;
  description: String;
  marketingProperties: [CustomField];
  localizedSlugs: [LocalizedSlug];
  advantages: [CustomField];
  disadvantages: [CustomField];
  unitsOfMeasure: [UnitOfMeasure];
  id: String;
  sku: String;
  defaultImage: Image;
  images: [ResponsiveImage];
  priceInclTax: Money;
  priceExclTax: Money;
  salePriceInclTax: Money;
  salePriceExclTax: Money;
  recommendedPriceExclTax: Money;
  recommendedPriceInclTax: Money;
  stock: Int;
  customFields: [CustomField];
  productOptions?: [ProductOption];
  productAssociations?: [ProductAssociation];
  customizations?: [ProductCustomization];
  uomProperty: String;
}

export interface UnitOfMeasure {
  itemName: String;
  defaultShipping: String;
  uomProperty: String;
  primary: String;
  weight: String;
  discount: String;
  length: String;
  height: String;
  width: String;
  market: String;
  quantity: String;
  priceInclTax: Money;
  priceExclTax: Money;
  salePriceInclTax: Money;
  salePriceExclTax: Money;
  availability: Availability;
}

export interface Availability {
  stockAvailabilityMessage: ProductStockAvailabilityEnum;
  deliveryEstimateMessage: ProductDeliveryEstimateEnum;
  canAddToCart: Boolean;
  canLeaveEmail: Boolean;
  canLeaveEmailNoBackOrder: Boolean;
  deliveryDate: String;
  workingDays: String;
}

export interface ProductCustomization {
  id: String;
  title: String;
  minAmount: Int;
  maxAmount: Int;
  products: [ProductCustomizationProduct];
}

export interface ProductAssociation {
  code: String;
  products: [ProductAssociationProduct];
}

export interface ProductSlice {
  type: String;
  data: any;
}

export interface ProductCustomizationProduct {
  title: String;
  price: Money;
  productId: String;
}

export interface ProductAssociationProduct {
  key: String;
  quantity: Int;
}

export interface FilterInput {
  key: String;
  values: String[];
}

export interface KeyValuePairOfStringAndIEnumerableOfStringInput {
  key: String;
  value: String[];
}

export enum ProductOrder {
  ASCENDING = "ASCENDING",
  DESCENDING = "DESCENDING",
}

export enum ProductSort {
  NAME = "NAME",
  PRICE = "PRICE",
}

export interface WovarProductSortingInput {
  order: ProductOrder;
  sort: ProductSort;
}

export interface WovarOrder {
  orderItems: CheckoutCustomLineItem[];
  id: String;
  orderDate: String;
  orderNumber: String;
  status: String;
  erpOrderStatus?: string;
  purchaseReference?: string;
  billingAddress: BillingAddress;
  paid: Money;
  taxTotal: Money;
  discountTotal: Money;
  subtotalInclTax: Money;
  subtotalExclTax: Money;
  shippingCostExclTax: Money;
  shippingCostInclTax: Money;
  handlingCostInclTax: Money;
  handlingCostExclTax: Money;
  totalInclTax: Money;
  totalExclTax: Money;
}
export interface CheckoutCustomLineItem {
  id: String;
  sku: String;
  name: String;
  quantity: Int;
  price: Money;
  extendedPrice: Money;
}

export interface LanguageModel {
  domain: string;
  country: string;
  langsOptions: string[];
  defaultLang: string;
}

export enum ProductDeliveryEstimateEnum {
  DEFAULT = "DEFAULT",
  NOT_APPLICABLE = "NOT_APPLICABLE",
  DELIVERED_AT_SAME_DAY = "DELIVERED_AT_SAME_DAY",
  DELIVERED_ON_SPECIFIC_DATE = "DELIVERED_ON_SPECIFIC_DATE",
  DELIVERED_IN_X_WORKING_DAYS = "DELIVERED_IN_X_WORKING_DAYS",
  DELIVERED_AT_FOUR = "DELIVERED_AT_FOUR",
}

export enum ProductStockAvailabilityEnum {
  IMPOSSIBLE_TO_ORDER = "IMPOSSIBLE_TO_ORDER",
  IN_STOCK = "IN_STOCK",
  SOME_UNITS_AVAILABLE = "SOME_UNITS_AVAILABLE",
  OUT_OF_STOCK = "OUT_OF_STOCK",
}

export enum SlugCurrentPage {
  BLOG_CONTENT_PAGE = "BLOG_CONTENT_PAGE",
  CUSTOM_CONTENT_PAGE = "CUSTOM_CONTENT_PAGE",
  PRODUCT_DETAILS_PAGE = "PRODUCT_DETAILS_PAGE",
  CATEGORY_PAGE = "CATEGORY_PAGE",
  NO_RESPONSE_FOUND = "NO_RESPONSE_FOUND",
}

export interface ProductsAndCustomerContext {
  products: WovarProduct[];
  customer: WovarCustomer;
}

export type CartSnapshotItem = {
  sku: string;
  quantity: number;
  uomProperty?: string;
};

// Invoices (WOV-2167) — mirrors the backend customerInvoices / customerInvoice contract.

export type InvoiceStatus = "OPEN" | "PARTIALLY_PAID" | "PAID_IN_FULL";
export type InvoiceType = "INVOICE" | "CREDIT_NOTE";

export interface CustomerInvoice {
  id: string;
  invoiceNumber: string;
  type: InvoiceType;
  status: InvoiceStatus | null;
  amount: number | null;
  amountRemaining: number | null;
  currencyCode: string | null;
  orderId: number | null;
  orderNumber: string | null;
  purchaseReference: string | null;
  invoiceDate: string | null;
  // Only set for pay-on-account invoices.
  dueDate: string | null;
}

export interface CustomerInvoiceLine {
  name: string;
  quantity: number | null;
  unitPriceExclTax: number | null;
  totalExclTax: number | null;
}

// Only the single-invoice query fills the tax breakdown.
export interface CustomerInvoiceDetail extends CustomerInvoice {
  subtotalExclTax: number | null;
  taxTotal: number | null;
  taxRate: number | null;
  itemsSubtotalExclTax: number | null;
  discountTotalExclTax: number | null;
  shippingCostExclTax: number | null;
  lines: CustomerInvoiceLine[];
}

export interface CustomerInvoicesResult {
  invoices: CustomerInvoice[];
  creditLimit: number | null;
  outstandingTotal: number;
  currencyCode: string | null;
  hasMore: boolean;
}

// Billed-to block, composed from the signed-in customer rather than from NetSuite.
export interface InvoiceParty {
  company: string;
  addressLine1: string;
  postalCode: string;
  city: string;
  country: string;
  kvkNumber: string;
  vatNumber: string;
  email: string;
}

// Credit space above the invoice list; available is derived here.
export interface CustomerCreditSummary {
  creditLimit: number;
  outstanding: number;
  available: number;
  currencyCode: string | null;
}
